import { TextField } from '@material-ui/core';
import { icon_user_circle } from 'components/base/images';
import MuiButton from 'components/common/button/MuiButton';
import MuiWrapper from 'components/common/input/MuiWrapper';
import CustomSpan from 'components/common/text/CustomSpan';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { color } from 'styles/utils';
import cx from 'classnames';
import { PartnerActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import useFetchLoading from 'lib/hooks/useFetchLoading';
import { useDidUpdateEffect } from 'lib/utils';
import useInput from 'lib/hooks/useInput';
import InfiniteScroll from 'react-infinite-scroll-component';
import CircularLoading from 'components/base/loading/CircularLoading';
import ImgCrop from 'components/common/images/ImgCrop';
import { capitalize } from 'lib/library';
import T from 'components/common/text/T';
import { useTranslation } from 'react-i18next';
import { partnerTypeList } from 'lib/mapper';

interface GoldStatusModalContainerProps {
  partnershipIdx?: number | null;
  partnershipCode?: string | null;
  partnerGroupCode?: string | null;
  partnerGroupIdx?: number | null;
  onClose?: () => void;
}

interface goldStatusFormInput {
  // submitValue - current, remained
  currentInlay: number;
  currentAType: number;
  currentSType: number;
  currentPtGold: number;
  // remainedInlay: number;
  // remainedAType: number;
  // remainedSType: number;
  // remainedPtGold: number;
  // state
  usedInlay: number;
  wastedInlay: number;
  returnedInlay: number;
  usedAType: number;
  wastedAType: number;
  returnedAType: number;
  usedSType: number;
  wastedSType: number;
  returnedSType: number;
  usedPtGold: number;
  wastedPtGold: number;
  returnedPtGold: number;
}

interface Unit {
  _id: string;
  current: number;
  use: number;
  consume: number;
  return: number;
  remain: number;
}

interface History {
  _id: string;
  projectCode: string;
  projectName: string;
  updaterName: string;
  uadaterSyncUid: string;
  enrollDate: number;
  inlay: Unit;
  aType: Unit;
  sType: Unit;
  ptGold: Unit;
}

interface GoldInfo {
  aType: number;
  inlay: number;
  modifyDate: number;
  ptGold: number;
  sType: number;
}

interface PartnerInfo {
  email: string;
  name: string;
  profileImg: string | null;
  userTypeIdx: number;
}

function GoldStatusModalContainer({
  partnershipIdx,
  partnershipCode,
  partnerGroupCode,
  partnerGroupIdx,
  onClose = () => {},
}: GoldStatusModalContainerProps) {
  const {
    language,
    partnerGoldData,
    partnerGoldSuccess,
    editPartnerGoldPending,
    editPartnerGoldSuccess,
    partnerGoldHistoriesData,
  } = useShallowAppSelector(state => ({
    language: state.base.language,
    partnerGoldData: state.partner.partnerGold.data || {},
    partnerGoldSuccess: state.partner.partnerGold.success,
    editPartnerGoldPending: state.partner.editPartnerGold.pending,
    editPartnerGoldSuccess: state.partner.editPartnerGold.success,
    partnerGoldHistoriesData: state.partner.partnerGoldHistories.data,
  }));
  const { t } = useTranslation();
  const goldInfo: GoldInfo = partnerGoldData?.goldInfo;
  const partnerInfo: PartnerInfo = partnerGoldData?.partnerInfo;
  // const partnerGoldHistories = partnerGoldHistoriesData?.list;
  const [partnerGoldHistories, setPartnerGoldHistories] = useState<History[]>([]);
  const partnerGoldHistoriesPagingData = partnerGoldHistoriesData?.pagingData;
  const hasMorePartnerGoldHistories =
    partnerGoldHistoriesPagingData?.page < partnerGoldHistoriesPagingData?.totalPage;
  const [page, setPage] = useState(1);
  const setPartnerGoldHistoriesValue = useMemo(() => {
    if (!partnerGoldHistoriesData?.list?.length) return;
    return page === 1
      ? setPartnerGoldHistories(partnerGoldHistoriesData.list)
      : setPartnerGoldHistories(draft => [...draft, partnerGoldHistoriesData.list]);
  }, [page, partnerGoldHistoriesData?.list]);

  // gold state
  const {
    control: goldStatusControl,
    handleSubmit: handleSubmitGoldStatus,
    errors: goldStatusErrors,
    watch: goldStatusWatch,
    setValue: setGoldStatus,
    getValues: getGoldStatus,
  } = useForm<goldStatusFormInput>({
    mode: 'onChange',
    defaultValues: {
      currentInlay: 0,
      currentAType: 0,
      currentSType: 0,
      currentPtGold: 0,
      usedInlay: 0,
      wastedInlay: 0,
      returnedInlay: 0,
      usedAType: 0,
      wastedAType: 0,
      returnedAType: 0,
      usedSType: 0,
      wastedSType: 0,
      returnedSType: 0,
      usedPtGold: 0,
      wastedPtGold: 0,
      returnedPtGold: 0,
    },
  });
  const {
    currentInlay,
    currentAType,
    currentSType,
    currentPtGold,
    usedInlay,
    wastedInlay,
    returnedInlay,
    usedAType,
    wastedAType,
    returnedAType,
    usedSType,
    wastedSType,
    returnedSType,
    usedPtGold,
    wastedPtGold,
    returnedPtGold,
  } = goldStatusWatch();

  const remainedInlay = useMemo(() => {
    const remainedAmount = currentInlay - usedInlay - wastedInlay + returnedInlay;
    const parsedRemainedAmount = String(currentInlay - usedInlay - wastedInlay + returnedInlay);

    return parsedRemainedAmount.indexOf('.') !== -1
      ? Number(
          parsedRemainedAmount.split('.')[0] + '.' + parsedRemainedAmount.split('.')[1].slice(0, 2),
        )
      : remainedAmount;
  }, [currentInlay, usedInlay, wastedInlay, returnedInlay]);

  const remainedAType = useMemo(() => {
    const remainedAmount = currentAType - usedAType - wastedAType + returnedAType;
    const parsedRemainedAmount = String(currentAType - usedAType - wastedAType + returnedAType);

    return parsedRemainedAmount.indexOf('.') !== -1
      ? Number(
          parsedRemainedAmount.split('.')[0] + '.' + parsedRemainedAmount.split('.')[1].slice(0, 2),
        )
      : remainedAmount;
  }, [currentAType, usedAType, wastedAType, returnedAType]);

  const remainedSType = useMemo(() => {
    const remainedAmount = currentSType - usedSType - wastedSType + returnedSType;
    const parsedRemainedAmount = String(currentSType - usedSType - wastedSType + returnedSType);

    return parsedRemainedAmount.indexOf('.') !== -1
      ? Number(
          parsedRemainedAmount.split('.')[0] + '.' + parsedRemainedAmount.split('.')[1].slice(0, 2),
        )
      : remainedAmount;
  }, [currentSType, usedSType, wastedSType, returnedSType]);

  const remainedPtGold = useMemo(() => {
    const remainedAmount = currentPtGold - usedPtGold - wastedPtGold + returnedPtGold;
    const parsedRemainedAmount = String(currentPtGold - usedPtGold - wastedPtGold + returnedPtGold);

    return parsedRemainedAmount.indexOf('.') !== -1
      ? Number(
          parsedRemainedAmount.split('.')[0] + '.' + parsedRemainedAmount.split('.')[1].slice(0, 2),
        )
      : remainedAmount;
  }, [currentPtGold, usedPtGold, wastedPtGold, returnedPtGold]);

  const [logOrder, setLogOrder] = useState(0); // 0: Latest, 1: Oldest
  const isChangedUnit = useMemo(() => {
    let isChangedGoldInfo = false;
    const originGoldInfo: number[] = [
      goldInfo?.inlay,
      goldInfo?.aType,
      goldInfo?.sType,
      goldInfo?.ptGold,
    ];
    const changedUnits: number[] = [
      usedInlay,
      wastedInlay,
      returnedInlay,
      usedAType,
      wastedAType,
      returnedAType,
      usedSType,
      wastedSType,
      returnedSType,
      usedPtGold,
      wastedPtGold,
      returnedPtGold,
    ];
    const changedGoldInfo: number[] = [currentInlay, currentAType, currentSType, currentPtGold];

    for (let i = 0; i < changedGoldInfo.length; i++) {
      if (changedGoldInfo[i] !== originGoldInfo[i]) isChangedGoldInfo = true;
    }

    return isChangedGoldInfo || changedUnits.some(item => !!item);
  }, [goldStatusWatch, goldInfo]);

  // method
  const handleSaveGold = () => {
    PartnerActions.edit_partner_gold_request({
      partnershipIdx,
      partnershipCode,
      partnerGroupCode,
      partnerGroupIdx,
      // projectCode
      // projectName
      inlay: {
        current: currentInlay,
        use: usedInlay,
        consume: wastedInlay,
        return: returnedInlay,
        remain: remainedInlay,
      },
      aType: {
        current: currentAType,
        use: usedAType,
        consume: wastedAType,
        return: returnedAType,
        remain: remainedAType,
      },
      sType: {
        current: currentSType,
        use: usedSType,
        consume: wastedSType,
        return: returnedSType,
        remain: remainedSType,
      },
      ptGold: {
        current: currentPtGold,
        use: usedPtGold,
        consume: wastedPtGold,
        return: returnedPtGold,
        remain: remainedPtGold,
      },
    });
  };

  const onFetchPartnerGoldHistories = () => {
    // console.log('call onFetchPartnerGoldHistories');
    if (!hasMorePartnerGoldHistories) return;
    const nextPage = page + 1;
    PartnerActions.fetch_partner_gold_histories_request({
      // id: partnershipCode,
      id: partnerGroupCode,
      data: { page: nextPage },
    });
    setPage(nextPage);
  };

  // SECTION: DidMount

  useEffect(() => {
    // if (!partnershipCode) return;
    // console.log('PartnerActions', PartnerActions.fetch_partner_gold_request(partnershipCode));
    // PartnerActions.fetch_partner_gold_histories_request({ id: partnershipCode, data: { page: 1 } });

    if (!partnerGroupCode) return;
    // console.log('PartnerActions', PartnerActions.fetch_partner_gold_request(partnerGroupCode));
    PartnerActions.fetch_partner_gold_request(partnerGroupCode);
    PartnerActions.fetch_partner_gold_histories_request({
      id: partnerGroupCode,
      data: { page: 1 },
    });
  }, []);

  // SECTION: DidUpdate

  // set goldInfo, when fetch success
  useDidUpdateEffect(() => {
    if (goldInfo) {
      setGoldStatus('currentInlay', goldInfo?.inlay || 0);
      setGoldStatus('currentAType', goldInfo?.aType || 0);
      setGoldStatus('currentSType', goldInfo?.sType || 0);
      setGoldStatus('currentPtGold', goldInfo?.ptGold || 0);
      return;
    }
  }, [goldInfo]);

  // close modal, when edit success
  useDidUpdateEffect(() => {
    if (editPartnerGoldSuccess) onClose();
  }, [!!editPartnerGoldSuccess]);

  useEffect(() => {
    return () => {
      PartnerActions.fetch_partner_gold_histories_clear();
    };
  }, []);

  // useEffect(() => {
  //   console.log('partnerInfo ____ ', partnerInfo);
  // }, [partnerInfo]);

  // const { isFetchSuccess } = useFetchLoading({ partnerGoldSuccess });
  // if (!isFetchSuccess) return null;

  return (
    <StyledGoldStatusModalContainer data-component-name="GoldStatusModalContainer">
      {/* goldStatus */}
      <div className="goldStatusModal__gold_form_container">
        <div className="goldStatusModal__profile_box">
          <div className="goldStatusModal__profile_in">
            <figure className="goldStatusModal__profile">
              {/* <img src={icon_user_circle} alt="profile" /> */}
              {partnerInfo?.profileImg ? (
                <ImgCrop width={100} isCircle src={partnerInfo?.profileImg} />
              ) : (
                <img src={icon_user_circle} alt="profile" />
              )}
            </figure>
            <p className="goldStatusModal__nickname text-overflow-ellipsis">{partnerInfo?.name}</p>
            <p className="goldStatusModal__clinic text-overflow-ellipsis">
              {/* <T>GLOBAL_TYPE</T>: {capitalize(partnerInfo?.partnerType)} */}
              <T>GLOBAL_TYPE</T>:{' '}
              <T>{partnerTypeList.find(i => i.id === partnerInfo?.userTypeIdx)?.text}</T>
              {/* <T>GLOBAL_TYPE</T>: {capitalize(partnerInfo?.userTypeIdx)} */}
            </p>
            <p className="goldStatusModal__email text-overflow-ellipsis">{partnerInfo?.email}</p>
          </div>
        </div>

        <div className="goldStatusModal__form_box">
          <div className="goldStatusModal__clinic">
            <div>
              <CustomSpan fontColor={color.blue}>{partnerInfo?.name}</CustomSpan>
              <CustomSpan fontColor="#B5B7C1">
                <T>GOLD_CURR_HOLDING_TEXT</T>
              </CustomSpan>
            </div>
            <div>
              <CustomSpan fontColor={color.blue} fontSize={11}>
                {/* {moment.unix(goldInfo?.modifyDate).format('MMM. DD. HH:mm')} */}
                {!!goldInfo?.modifyDate && language === 'en'
                  ? moment.unix(goldInfo?.modifyDate).locale(language).format('MMM DD, YYYY')
                  : moment.unix(goldInfo?.modifyDate).locale(language).format('YYYY. MM. DD ')}
              </CustomSpan>
            </div>
          </div>
          <div className="goldStatusModal__form_table_box">
            <table className="goldStatusModal__form_table">
              <colgroup>
                <col />
                <col />
                <col />
                <col />
                <col />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th></th>
                  <th>
                    <T>GOLD_STATUS_TYPE_INRAY</T>
                  </th>
                  <th>
                    <T>GOLD_STATUS_TYPE_A</T>
                  </th>
                  <th>
                    <T>GOLD_STATUS_TYPE_S</T>
                  </th>
                  <th>
                    <T>GOLD_STATUS_TYPE_PT</T>
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>
                    <T>GLOBAL_CURRENT</T>
                  </th>
                  <td>
                    <Controller
                      name="currentInlay"
                      control={goldStatusControl}
                      // rules={{ pattern: /[^0-9]/g }}
                      render={({ onChange, value }) => (
                        <MuiWrapper className="sm">
                          <TextField
                            className="radius-md form__input_box"
                            type="number"
                            variant="outlined"
                            fullWidth
                            placeholder="0"
                            error={goldStatusErrors.currentInlay?.type === 'pattern'}
                            value={value || ''}
                            onChange={e => onChange(Number(e.target.value))}
                          />
                        </MuiWrapper>
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      name="currentAType"
                      control={goldStatusControl}
                      // rules={{ pattern: /[^0-9]g/ }}
                      render={({ onChange, value }) => (
                        <MuiWrapper className="sm">
                          <TextField
                            className="radius-md form__input_box"
                            type="number"
                            variant="outlined"
                            fullWidth
                            placeholder="0"
                            error={goldStatusErrors.currentAType?.type === 'pattern'}
                            value={value || ''}
                            onChange={e => onChange(Number(e.target.value))}
                          />
                        </MuiWrapper>
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      name="currentSType"
                      control={goldStatusControl}
                      // rules={{ pattern: /[^0-9]/g }}
                      render={({ onChange, value }) => (
                        <MuiWrapper className="sm">
                          <TextField
                            className="radius-md form__input_box"
                            type="number"
                            variant="outlined"
                            fullWidth
                            placeholder="0"
                            error={goldStatusErrors.currentSType?.type === 'pattern'}
                            value={value || ''}
                            onChange={e => onChange(Number(e.target.value))}
                          />
                        </MuiWrapper>
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      name="currentPtGold"
                      control={goldStatusControl}
                      // rules={{ pattern: /[^0-9]/g }}
                      render={({ onChange, value }) => (
                        <MuiWrapper className="sm">
                          <TextField
                            className="radius-md form__input_box"
                            type="number"
                            variant="outlined"
                            fullWidth
                            placeholder="0"
                            error={goldStatusErrors.currentPtGold?.type === 'pattern'}
                            value={value || ''}
                            onChange={e => onChange(Number(e.target.value))}
                          />
                        </MuiWrapper>
                      )}
                    />
                  </td>
                </tr>

                <tr>
                  <th>
                    <T>GLOBAL_USAGE</T>
                  </th>
                  <td>
                    <Controller
                      name="usedInlay"
                      control={goldStatusControl}
                      // rules={{ pattern: /[^0-9]/g }}
                      render={({ onChange, value }) => (
                        <MuiWrapper className="sm">
                          <TextField
                            className="radius-md form__input_box"
                            type="number"
                            variant="outlined"
                            fullWidth
                            placeholder="0"
                            error={goldStatusErrors.usedInlay?.type === 'pattern'}
                            value={value || ''}
                            onChange={e => onChange(Number(e.target.value))}
                            // onFocus={() => setGoldStatus('usedInlay', '')}
                          />
                        </MuiWrapper>
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      name="usedAType"
                      control={goldStatusControl}
                      // rules={{ pattern: /[^0-9]/g }}
                      render={({ onChange, value }) => (
                        <MuiWrapper className="sm">
                          <TextField
                            className="radius-md form__input_box"
                            type="number"
                            variant="outlined"
                            fullWidth
                            placeholder="0"
                            error={goldStatusErrors.usedAType?.type === 'pattern'}
                            value={value || ''}
                            onChange={e => onChange(Number(e.target.value))}
                          />
                        </MuiWrapper>
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      name="usedSType"
                      control={goldStatusControl}
                      // rules={{ pattern: /[^0-9]/g }}
                      render={({ onChange, value }) => (
                        <MuiWrapper className="sm">
                          <TextField
                            className="radius-md form__input_box"
                            type="number"
                            variant="outlined"
                            fullWidth
                            placeholder="0"
                            error={goldStatusErrors.usedSType?.type === 'pattern'}
                            value={value || ''}
                            onChange={e => onChange(Number(e.target.value))}
                          />
                        </MuiWrapper>
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      name="usedPtGold"
                      control={goldStatusControl}
                      // rules={{ pattern: /[^0-9]/g }}
                      render={({ onChange, value }) => (
                        <MuiWrapper className="sm">
                          <TextField
                            className="radius-md form__input_box"
                            type="number"
                            variant="outlined"
                            fullWidth
                            placeholder="0"
                            error={goldStatusErrors.usedPtGold?.type === 'pattern'}
                            value={value || ''}
                            onChange={e => onChange(Number(e.target.value))}
                          />
                        </MuiWrapper>
                      )}
                    />
                  </td>
                </tr>

                <tr>
                  <th>
                    <T>GLOBAL_CONSUMPTION</T>
                  </th>
                  <td>
                    <Controller
                      name="wastedInlay"
                      control={goldStatusControl}
                      // rules={{ pattern: /[^0-9]/g }}
                      render={({ onChange, value }) => (
                        <MuiWrapper className="sm">
                          <TextField
                            className="radius-md form__input_box"
                            type="number"
                            variant="outlined"
                            fullWidth
                            placeholder="0"
                            error={goldStatusErrors.wastedInlay?.type === 'pattern'}
                            value={value || ''}
                            onChange={e => onChange(Number(e.target.value))}
                          />
                        </MuiWrapper>
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      name="wastedAType"
                      control={goldStatusControl}
                      // rules={{ pattern: /[^0-9]/g }}
                      render={({ onChange, value }) => (
                        <MuiWrapper className="sm">
                          <TextField
                            className="radius-md form__input_box"
                            type="number"
                            variant="outlined"
                            fullWidth
                            placeholder="0"
                            error={goldStatusErrors.wastedAType?.type === 'pattern'}
                            value={value || ''}
                            onChange={e => onChange(Number(e.target.value))}
                          />
                        </MuiWrapper>
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      name="wastedSType"
                      control={goldStatusControl}
                      // rules={{ pattern: /[^0-9]/g }}
                      render={({ onChange, value }) => (
                        <MuiWrapper className="sm">
                          <TextField
                            className="radius-md form__input_box"
                            type="number"
                            variant="outlined"
                            fullWidth
                            placeholder="0"
                            error={goldStatusErrors.wastedSType?.type === 'pattern'}
                            value={value || ''}
                            onChange={e => onChange(Number(e.target.value))}
                          />
                        </MuiWrapper>
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      name="wastedPtGold"
                      control={goldStatusControl}
                      // rules={{ pattern: /[^0-9]/g }}
                      render={({ onChange, value }) => (
                        <MuiWrapper className="sm">
                          <TextField
                            className="radius-md form__input_box"
                            type="number"
                            variant="outlined"
                            fullWidth
                            placeholder="0"
                            error={goldStatusErrors.wastedPtGold?.type === 'pattern'}
                            value={value || ''}
                            onChange={e => onChange(Number(e.target.value))}
                          />
                        </MuiWrapper>
                      )}
                    />
                  </td>
                </tr>

                <tr>
                  <th>
                    <T>GLOBAL_RETURN</T>
                  </th>
                  <td>
                    <Controller
                      name="returnedInlay"
                      control={goldStatusControl}
                      // rules={{ pattern: /[^0-9]/g }}
                      render={({ onChange, value }) => (
                        <MuiWrapper className="sm">
                          <TextField
                            className="radius-md form__input_box"
                            type="number"
                            variant="outlined"
                            fullWidth
                            placeholder="0"
                            error={goldStatusErrors.returnedInlay?.type === 'pattern'}
                            value={value || ''}
                            onChange={e => onChange(Number(e.target.value))}
                          />
                        </MuiWrapper>
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      name="returnedAType"
                      control={goldStatusControl}
                      // rules={{ pattern: /[^0-9]/g }}
                      render={({ onChange, value }) => (
                        <MuiWrapper className="sm">
                          <TextField
                            className="radius-md form__input_box"
                            type="number"
                            variant="outlined"
                            fullWidth
                            placeholder="0"
                            error={goldStatusErrors.returnedAType?.type === 'pattern'}
                            value={value || ''}
                            onChange={e => onChange(Number(e.target.value))}
                          />
                        </MuiWrapper>
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      name="returnedSType"
                      control={goldStatusControl}
                      // rules={{ pattern: /[^0-9]/g }}
                      render={({ onChange, value }) => (
                        <MuiWrapper className="sm">
                          <TextField
                            className="radius-md form__input_box"
                            type="number"
                            variant="outlined"
                            fullWidth
                            placeholder="0"
                            error={goldStatusErrors.returnedSType?.type === 'pattern'}
                            value={value || ''}
                            onChange={e => onChange(Number(e.target.value))}
                          />
                        </MuiWrapper>
                      )}
                    />
                  </td>
                  <td>
                    <Controller
                      name="returnedPtGold"
                      control={goldStatusControl}
                      // rules={{ pattern: /[^0-9]/g }}
                      render={({ onChange, value }) => (
                        <MuiWrapper className="sm">
                          <TextField
                            className="radius-md form__input_box"
                            type="number"
                            variant="outlined"
                            fullWidth
                            placeholder="0"
                            error={goldStatusErrors.returnedPtGold?.type === 'pattern'}
                            value={value || ''}
                            onChange={e => onChange(Number(e.target.value))}
                          />
                        </MuiWrapper>
                      )}
                    />
                  </td>
                </tr>

                <tr>
                  <th>
                    <T>GLOBAL_AVAILABLE</T>
                  </th>
                  <td>
                    <div className="flex-center">
                      <span className="form_input_text">{remainedInlay}</span>
                    </div>
                  </td>
                  <td>
                    <div className="flex-center">
                      <span className="form_input_text">{remainedAType}</span>
                    </div>
                  </td>
                  <td>
                    <div className="flex-center">
                      <span className="form_input_text">{remainedSType}</span>
                    </div>
                  </td>
                  <td>
                    <div className="flex-center">
                      <span className="form_input_text">{remainedPtGold}</span>
                    </div>
                  </td>
                  <td>
                    <MuiButton
                      config={{
                        width: 130,
                      }}
                      disabled={!isChangedUnit || editPartnerGoldPending}
                      variant="contained"
                      color="secondary"
                      disableElevation
                      className="sm inset-shadow-default border-radius-round"
                      onClick={handleSubmitGoldStatus(handleSaveGold)}
                    >
                      <b>
                        <T>GLOBAL_SAVE</T>
                      </b>
                    </MuiButton>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* log */}
      <div className="goldStatusModal__log_container">
        <div className="goldStatusModal__log_title_box">
          <div className="goldStatusModal__log_title">
            <T>GLOBAL_LOG</T>
          </div>
          {/* <div className="goldStatusModal__log_order">
            <button
              className={cx('btn-reset', { active: logOrder === 0 })}
              onClick={() => setLogOrder(0)}
            >
              Latest
            </button>
            <pre>{'  l  '}</pre>
            <button
              className={cx('btn-reset', { active: logOrder === 1 })}
              onClick={() => setLogOrder(1)}
            >
              Oldest
            </button>
          </div> */}
        </div>

        <div className="goldStatusModal__log_content_container">
          <ul className="goldStatusModal__log_list">
            <InfiniteScroll
              className="dashboard__notifications_scroll infinite-scroll-box"
              dataLength={partnerGoldHistories?.length}
              next={onFetchPartnerGoldHistories}
              hasMore={hasMorePartnerGoldHistories}
              // next={() => {}}
              // hasMore={true}
              loader={
                <div className="infinite-scroll-loading">
                  <CircularLoading />
                </div>
              }
              height={250}
              // height={205}
            >
              {!!partnerGoldHistories?.length &&
                partnerGoldHistories.map((item, idx) => {
                  const { inlay, aType, sType, ptGold } = item;
                  return (
                    <li className="goldStatusModal__log_item" key={idx}>
                      <div className="goldStatusModal__log_project_text_box">
                        <div className="goldStatusModal__log_project_text">
                          <T>GOLD_STATUS_TYPE_INRAY_LOG</T>: {inlay.current} / {inlay.use} /{' '}
                          {inlay.consume} / {inlay.return} / {inlay.remain} ,{' '}
                          <T>GOLD_STATUS_TYPE_A_LOG</T>: {aType.current} / {aType.use} /{' '}
                          {aType.consume} / {aType.return} / {aType.remain} ,{' '}
                          <T>GOLD_STATUS_TYPE_S_LOG</T>: {sType.current} / {sType.use} /{' '}
                          {sType.consume} / {sType.return} / {sType.remain} ,{' '}
                          <T>GOLD_STATUS_TYPE_PT_LOG</T>: {ptGold.current} / {ptGold.use} /{' '}
                          {ptGold.consume} / {ptGold.return} / {ptGold.remain}
                        </div>
                        <p className="goldStatusModal__log_project_date">
                          {/* {moment.unix(item.enrollDate).format('MMM. DD. YYYY HH:mm')}{' '} */}
                          {language === 'en'
                            ? moment.unix(item.enrollDate).locale(language).format('MMM DD, YYYY')
                            : moment
                                .unix(item.enrollDate)
                                .locale(language)
                                .format('YYYY. MM. DD ')}{' '}
                          {item.updaterName}
                        </p>
                      </div>
                    </li>
                  );
                })}
            </InfiniteScroll>

            {/* {!!partnerGoldHistories?.length &&
              partnerGoldHistories.map((item, idx) => {
                const { inlay, aType, sType, ptGold } = item;
                return (
                  <li className="goldStatusModal__log_item" key={idx}>
                    <div className="goldStatusModal__log_project_text_box">
                      <div className="goldStatusModal__log_project_text">
                        inlay: {inlay.current} / {inlay.use} / {inlay.consume} / {inlay.return} /{' '}
                        {inlay.remain} , aType: {aType.current} / {aType.use} / {aType.consume} /{' '}
                        {aType.return} / {aType.remain} , sType: {sType.current} / {sType.use} /{' '}
                        {sType.consume} / {sType.return} / {sType.remain} , ptGold: {ptGold.current}{' '}
                        / {ptGold.use} / {ptGold.consume} / {ptGold.return} / {ptGold.remain}
                      </div>
                      <p className="goldStatusModal__log_project_date">
                        {moment.unix(item.enrollDate).format('MMM. DD. YYYY HH:mm')}{' '}
                        {item.updaterName}
                      </p>
                    </div>
                  </li>
                );
              })} */}
          </ul>
        </div>
      </div>
    </StyledGoldStatusModalContainer>
  );
}

const StyledGoldStatusModalContainer = styled.div`
  .goldStatusModal__gold_form_container {
    display: flex;
    justify-content: space-between;
    .goldStatusModal__profile_box {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 265px;
      height: 290px;
      padding: 0 10px;
      background-color: #fafafd;
      border-radius: 10px;
      .goldStatusModal__profile_in {
        margin-top: -10px;
        width: 100%;
        text-align: center;
      }
      .goldStatusModal__profile {
        img {
          width: 100px;
          height: 100px;
        }
      }
      .goldStatusModal__nickname {
        margin-top: 30px;
        font-size: 18px;
        font-weight: 700;
      }
      .goldStatusModal__clinic {
        margin-top: 15px;
      }
      .goldStatusModal__email {
        margin-top: 10px;
        font-size: 16px;
      }
    }

    .goldStatusModal__form_box {
      width: calc(100% - 295px);

      .goldStatusModal__clinic {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        padding-bottom: 5px;
        border-bottom: 1px solid ${color.blue};
      }
      .goldStatusModal__form_table_box {
        padding-top: 10px;
        .goldStatusModal__form_table {
          .muiWrapper {
            width: 78px;
            .MuiInputBase-input {
              text-align: center;
            }
          }

          th {
            color: #b5b7c1;
            font-size: 13px;
            text-align: center;
          }
          thead {
            th {
              padding: 8px 0;
            }
          }
          tbody {
            th,
            td {
              /* vertical-align: middle; */
            }
            th {
              text-align: right;
            }
            td {
              padding: 10px 15px;
            }
            tr:first-child {
              td {
                padding-top: 0;
              }
            }
            tr:last-child {
              td {
                padding-top: 0;
                padding-bottom: 0;
                &:last-child {
                  padding: 0;
                  padding-left: 15px;
                }
              }
            }
          }
        }
      }

      .form__input_box {
      }
      .form_input_text {
        display: inline-block;
        width: 78px;
        text-align: center;
        font-size: 14px;
        overflow-y: auto;
      }
    }
  }

  .goldStatusModal__log_container {
    margin-top: 55px;

    .goldStatusModal__log_title_box {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      .goldStatusModal__log_title {
        font-size: 21px;
        font-weight: 500;
      }
      .goldStatusModal__log_order {
        font-size: 14px;
        color: #858997;
        pre {
          display: inline;
        }
        button {
          &.active {
            color: ${color.blue};
            font-weight: 500;
          }
        }
      }
    }

    .goldStatusModal__log_content_container {
      position: relative;
      margin-top: 10px;
      padding: 30px;
      min-height: 266px;
      border: 1px solid #b5b7c1;
      border-radius: 15px;

      .goldStatusModal__log_list {
        .goldStatusModal__log_item {
          display: flex;
          column-gap: 30px;
          font-size: 13px;

          &:not(:first-child) {
            margin-top: 20px;
          }
          .goldStatusModal__log_project_name {
            width: 60px;
            color: ${color.blue};
          }
          .goldStatusModal__log_project_text_box {
            /* width: calc(100% - 90px); */
            width: 100%;
            .goldStatusModal__log_project_date {
              margin-top: 5px;
              font-size: 11px;
              color: #9194a4;
            }
          }
        }
      }
    }
  }
`;

export default React.memo(GoldStatusModalContainer);
